/**
 * Estado de interface do Modo Sofá (lobby, pausa, HUD, anúncios).
 *
 * A partida em si NÃO mora aqui: o objeto `SofaMatch` é mutado 60x/s pelo
 * diretor e lido direto pelos componentes 3D. Esta store só guarda o que
 * o React precisa desenhar — escolhas do lobby e um retrato do HUD
 * publicado ~12x/s, para não re-renderizar a árvore a cada quadro.
 */
import { create } from "zustand";
import type { TankFamilyId } from "@/lib/types/tanks";
import { TANK_FAMILY_IDS } from "@/lib/data/tankFamilies";
import type { TerrainTheme } from "@/lib/waveGenerator";
import {
  MAX_SOFA_PLAYERS,
  SOFA,
  type SeatIndex,
  type SofaCpuLevel,
  type SofaDifficulty,
  type SofaItemKind,
  type SofaMode,
  type SofaPhase,
  type SofaSpecial,
} from "./sofaSim";
import { SOFA_DIFFICULTIES } from "./sofaContent";
import type { SofaDevice } from "./sofaInput";
import { familySpecials, loadRecords, type SofaRecords } from "./sofaSetup";
import { loadShowFps, saveShowFps } from "./sofaPerf";

/** Cores fixas por assento: identidade na tela dividida vale mais que cosmético. */
export const SOFA_SEAT_COLORS = ["#22d3ee", "#fb923c", "#a3e635", "#f472b6"] as const;

export const SOFA_PLAYER_COUNTS = [2, 3, 4] as const;
export type SofaPlayerCount = (typeof SOFA_PLAYER_COUNTS)[number];

export const SOFA_THEMES: TerrainTheme[] = [
  "industrial",
  "desert",
  "arctic",
  "forest",
  "volcanic",
  "cyber",
];

export const SOFA_MODES: SofaMode[] = ["coop", "rivals", "versus"];

/**
 * Uma vaga do sofá. Dentro de `playerCount`, vaga com dispositivo é humana e
 * vaga sem dispositivo é CPU; fora dele, a vaga está desligada.
 */
export interface SofaSeatConfig {
  device: SofaDevice | null;
  family: TankFamilyId;
  /** Índice no pool de poderes da família (ver familySpecials). */
  specialIndex: number;
}

export interface SofaHudSeat {
  health: number;
  maxHealth: number;
  alive: boolean;
  lives: number;
  eliminated: boolean;
  kills: number;
  deaths: number;
  score: number;
  charge: number;
  respawnIn: number;
  invulnerable: boolean;
  stealthed: boolean;
  special: SofaSpecial | null;
  /** Item guardado, esperando o botão (LB/X ou E/Q). */
  heldItem: SofaItemKind | null;
  /** Efeito ativo: tipo, segundos restantes, duração total e tiros que o Campo de Força ainda bloqueia. */
  effect: { kind: SofaItemKind; left: number; total: number; hits: number } | null;
  frozen: boolean;
  rescueProgress: number;
  accuracy: number;
  damageDealt: number;
  /** Tanque pilotado pela CPU. */
  cpu: boolean;
}

export interface SofaHud {
  phase: SofaPhase;
  mode: SofaMode;
  difficulty: SofaDifficulty;
  wave: number;
  bossWave: boolean;
  enemiesLeft: number;
  countdown: number;
  intermissionLeft: number;
  winner: SeatIndex | null;
  killsToWin: number;
  rivalsWaves: number;
  boss: { health: number; maxHealth: number } | null;
  /** Tempo de simulação (s) — banners de "Lutem!" e afins. */
  time: number;
  /** Um por jogador da partida (humanos e CPUs), na ordem dos assentos. */
  seats: SofaHudSeat[];
}

export interface SofaAnnouncement {
  id: number;
  text: string;
  color: string;
}

export interface SofaMatchResult {
  reward: number;
  newRecord: boolean;
}

export type SofaPauseReason = "user" | "disconnect";

/** Etapas do lobby (onboarding): conectar → escolher tanques → montar a partida. */
export type SofaLobbyStep = "join" | "tanks" | "setup";
export const SOFA_LOBBY_STEPS: SofaLobbyStep[] = ["join", "tanks", "setup"];
/**
 * Itens focáveis da etapa "setup", na ordem do direcional: os três cartões
 * de modo, a opção (dificuldade ou abates), a arena e o botão de começar.
 */
export const SETUP_FOCUS = { option: 3, arena: 4, start: 5 } as const;
export const SETUP_ROWS = 6;

interface SofaState {
  active: boolean;
  screen: "lobby" | "match";
  lobbyStep: SofaLobbyStep;
  /** Quantos tanques na partida (2 a 4). Vagas sem humano viram CPU. */
  playerCount: SofaPlayerCount;
  cpuLevel: SofaCpuLevel;
  /** Cada humano confirmou o tanque (etapa "tanks"); CPU está sempre pronta. */
  seatReady: boolean[];
  /** Linha focada pelo controle na etapa "setup". */
  setupFocus: number;
  mode: SofaMode;
  difficulty: SofaDifficulty;
  killsToWin: number;
  theme: TerrainTheme;
  /** Sempre 4 vagas; valem as primeiras `playerCount`. */
  seats: SofaSeatConfig[];
  records: SofaRecords;
  /** Muda a cada partida nova (revanche remonta a cena do zero). */
  matchKey: number;
  hud: SofaHud | null;
  result: SofaMatchResult | null;
  announcements: SofaAnnouncement[];
  paused: boolean;
  pauseReason: SofaPauseReason | null;
  disconnectedSeat: SeatIndex | null;
  /** Último dano por assento (timestamp de performance.now) — vinheta vermelha. */
  hitFlash: number[];
  /** Sobe quando um obstáculo leva dano ou cai — a cena redesenha só eles. */
  obstacleVersion: number;
  /** Botão focado pelo controle nas telas de pausa e resultado (como nos menus). */
  overlayFocus: number;
  /** Ao sair, o menu reabre na tela Jogar (de onde o sofá foi aberto). */
  returnToPlayMenu: boolean;
  /** Selo de FPS na partida (diagnóstico em aparelho real; salvo no aparelho). */
  showFps: boolean;

  open: (p1Family?: TankFamilyId, p1Special?: string | null) => void;
  setLobbyStep: (step: SofaLobbyStep) => void;
  setSeatReady: (seat: SeatIndex, ready: boolean) => void;
  setSetupFocus: (index: number) => void;
  /** Voltar do lobby: um passo para trás, ou sai do sofá no primeiro. */
  lobbyBack: () => void;
  close: () => void;
  setPlayerCount: (count: SofaPlayerCount) => void;
  setCpuLevel: (level: SofaCpuLevel) => void;
  setMode: (mode: SofaMode) => void;
  cycleMode: (dir: 1 | -1) => void;
  cycleOption: (dir: 1 | -1) => void;
  setDifficulty: (difficulty: SofaDifficulty) => void;
  setKillsToWin: (kills: number) => void;
  setTheme: (theme: TerrainTheme) => void;
  cycleTheme: (dir: 1 | -1) => void;
  /**
   * Entra numa vaga: a primeira CPU dentro da contagem; se todas já são
   * humanas, abre a próxima (até 4). Com `at`, ocupa essa vaga se ela for
   * CPU (clique numa vaga). Devolve a vaga ou -1 (sofá cheio).
   */
  joinSeat: (device: SofaDevice, at?: SeatIndex) => SeatIndex | -1;
  /** O humano sai: a vaga volta a ser CPU. */
  releaseSeat: (seat: SeatIndex) => void;
  setFamily: (seat: SeatIndex, family: TankFamilyId) => void;
  cycleFamily: (seat: SeatIndex, dir: 1 | -1) => void;
  cycleSpecial: (seat: SeatIndex, dir: 1 | -1) => void;
  /** Sorteia tanque e poder de uma CPU (ou de todas, sem argumento). */
  shuffleCpu: (seat?: SeatIndex) => void;
  startMatch: () => void;
  rematch: () => void;
  backToLobby: () => void;
  setPaused: (paused: boolean, reason?: SofaPauseReason, seat?: SeatIndex | null) => void;
  setHud: (hud: SofaHud) => void;
  setResult: (result: SofaMatchResult) => void;
  flashHit: (seat: SeatIndex) => void;
  bumpObstacles: () => void;
  setOverlayFocus: (index: number) => void;
  setShowFps: (on: boolean) => void;
  /** O Menu já abriu na tela Jogar: a marca não vale para a próxima montagem. */
  clearReturnToPlayMenu: () => void;
  announce: (text: string, color?: string) => void;
}

function cycle<T>(list: readonly T[], current: T, dir: 1 | -1): T {
  const i = list.indexOf(current);
  return list[(i + dir + list.length) % list.length];
}

/** Assentos que entram na partida, em ordem. */
export function participantSeats(state: { playerCount: number }): SeatIndex[] {
  return Array.from({ length: state.playerCount }, (_, i) => i as SeatIndex);
}

/** Assentos humanos (com controle ou teclado) dentro da contagem. */
export function humanSeats(state: {
  playerCount: number;
  seats: readonly SofaSeatConfig[];
}): SeatIndex[] {
  return participantSeats(state).filter((i) => state.seats[i].device !== null);
}

export function isCpuSeat(
  state: { playerCount: number; seats: readonly SofaSeatConfig[] },
  seat: number
): boolean {
  return seat < state.playerCount && state.seats[seat].device === null;
}

/** Dá para seguir/começar: pelo menos um humano no sofá. */
export function hasHuman(state: {
  playerCount: number;
  seats: readonly SofaSeatConfig[];
}): boolean {
  return humanSeats(state).length > 0;
}

/** Todos os humanos confirmaram o tanque. */
export function allHumansReady(state: {
  playerCount: number;
  seats: readonly SofaSeatConfig[];
  seatReady: readonly boolean[];
}): boolean {
  const humans = humanSeats(state);
  return humans.length > 0 && humans.every((i) => state.seatReady[i]);
}

const DEFAULT_FAMILIES: TankFamilyId[] = ["predator", "colossus", "venom", "storm"];

function emptySeats(): SofaSeatConfig[] {
  return DEFAULT_FAMILIES.map((family) => ({ device: null, family, specialIndex: 0 }));
}

let announcementId = 1;
const ANNOUNCEMENT_MS = 2600;
const MAX_ANNOUNCEMENTS = 3;

const FRESH_MATCH = {
  hud: null,
  result: null,
  announcements: [] as SofaAnnouncement[],
  paused: false,
  pauseReason: null,
  disconnectedSeat: null,
  hitFlash: [0, 0, 0, 0],
};

export const useSofa = create<SofaState>((set, get) => ({
  active: false,
  screen: "lobby",
  lobbyStep: "join",
  playerCount: 2,
  cpuLevel: "normal",
  seatReady: [false, false, false, false],
  setupFocus: 0,
  mode: "coop",
  difficulty: "normal",
  killsToWin: SOFA.VERSUS_KILLS,
  theme: "cyber",
  seats: emptySeats(),
  records: {},
  matchKey: 0,
  obstacleVersion: 0,
  overlayFocus: 0,
  returnToPlayMenu: false,
  showFps: loadShowFps(),
  ...FRESH_MATCH,

  open: (p1Family, p1Special) =>
    set((s) => {
      const family0 = p1Family && TANK_FAMILY_IDS.includes(p1Family) ? p1Family : s.seats[0].family;
      // P1 começa com o poder do tanque que já usa, se a família o oferece
      const special0 = p1Special ? familySpecials(family0).indexOf(p1Special as SofaSpecial) : -1;
      // Dispositivos são re-escolhidos a cada visita: índices de gamepad
      // mudam entre sessões e o lobby mostra quem é quem.
      const used = new Set<TankFamilyId>([family0]);
      const seats = s.seats.map((seat, i) => {
        if (i === 0) return { device: null, family: family0, specialIndex: Math.max(0, special0) };
        let family = seat.family;
        while (used.has(family)) family = cycle(TANK_FAMILY_IDS, family, 1);
        used.add(family);
        return { device: null, family, specialIndex: seat.specialIndex };
      });
      return {
        active: true,
        screen: "lobby",
        lobbyStep: "join",
        seatReady: [false, false, false, false],
        setupFocus: 0,
        records: loadRecords(),
        ...FRESH_MATCH,
        seats,
      };
    }),

  close: () => set({ active: false, screen: "lobby", returnToPlayMenu: true, ...FRESH_MATCH }),

  setPlayerCount: (count) =>
    set((s) => {
      if (count === s.playerCount) return {};
      // Menos vagas que humanos: os humanos se juntam nas primeiras vagas
      const humans = s.seats.filter((seat) => seat.device !== null);
      if (count >= s.playerCount || humans.length === 0) return { playerCount: count };
      const target = Math.max(count, humans.length) as SofaPlayerCount;
      const others = s.seats.filter((seat) => seat.device === null);
      const packed = [...humans, ...others].slice(0, MAX_SOFA_PLAYERS);
      return { playerCount: target, seats: packed, seatReady: [false, false, false, false] };
    }),
  setCpuLevel: (cpuLevel) => set({ cpuLevel }),

  setMode: (mode) => set({ mode }),
  cycleMode: (dir) => set((s) => ({ mode: cycle(SOFA_MODES, s.mode, dir) })),
  cycleOption: (dir) =>
    set((s) =>
      s.mode === "versus"
        ? { killsToWin: cycle(SOFA.KILLS_OPTIONS, s.killsToWin, dir) }
        : { difficulty: cycle(SOFA_DIFFICULTIES, s.difficulty, dir) }
    ),
  setDifficulty: (difficulty) => set({ difficulty }),
  setKillsToWin: (killsToWin) => set({ killsToWin }),
  setTheme: (theme) => set({ theme }),
  cycleTheme: (dir) => set((s) => ({ theme: cycle(SOFA_THEMES, s.theme, dir) })),

  joinSeat: (device, at) => {
    const s = get();
    const free = participantSeats(s).filter((i) => s.seats[i].device === null);
    let seat: SeatIndex | -1 =
      at !== undefined && free.includes(at) ? at : free.length > 0 ? free[0] : -1;
    let playerCount = s.playerCount;
    if (seat === -1 && s.playerCount < MAX_SOFA_PLAYERS) {
      seat = s.playerCount as SeatIndex;
      playerCount = (s.playerCount + 1) as SofaPlayerCount;
    }
    if (seat === -1) return -1;
    const seats = [...s.seats];
    seats[seat] = { ...seats[seat], device };
    const seatReady = [...s.seatReady];
    seatReady[seat] = false;
    set({ seats, playerCount, seatReady });
    return seat as SeatIndex;
  },

  releaseSeat: (seat) =>
    set((s) => {
      const seats = [...s.seats];
      seats[seat] = { ...seats[seat], device: null };
      const seatReady = [...s.seatReady];
      seatReady[seat] = false;
      // Sem nenhum humano não há partida: volta ao primeiro passo
      const anyHuman = seats.some((x, i) => i < s.playerCount && x.device !== null);
      return anyHuman ? { seats, seatReady } : { seats, seatReady, lobbyStep: "join" };
    }),

  setLobbyStep: (lobbyStep) =>
    set((s) =>
      lobbyStep === "tanks"
        ? { lobbyStep, seatReady: [false, false, false, false] }
        : lobbyStep === "setup"
          ? { lobbyStep, setupFocus: Math.max(0, SOFA_MODES.indexOf(s.mode)) }
          : { lobbyStep }
    ),
  lobbyBack: () => {
    const s = get();
    if (s.lobbyStep === "setup") s.setLobbyStep("tanks");
    else if (s.lobbyStep === "tanks") s.setLobbyStep("join");
    else s.close();
  },
  setSeatReady: (seat, ready) =>
    set((s) => {
      const seatReady = [...s.seatReady];
      seatReady[seat] = ready;
      return { seatReady };
    }),
  setSetupFocus: (setupFocus) => set({ setupFocus }),

  setFamily: (seat, family) =>
    set((s) => {
      if (s.seats[seat].family === family) return {};
      const seats = [...s.seats];
      seats[seat] = { ...seats[seat], family, specialIndex: 0 };
      return { seats };
    }),

  cycleFamily: (seat, dir) =>
    set((s) => {
      const seats = [...s.seats];
      seats[seat] = {
        ...seats[seat],
        family: cycle(TANK_FAMILY_IDS, seats[seat].family, dir),
        specialIndex: 0,
      };
      return { seats };
    }),

  cycleSpecial: (seat, dir) =>
    set((s) => {
      const count = familySpecials(s.seats[seat].family).length;
      if (count < 2) return {};
      const seats = [...s.seats];
      seats[seat] = {
        ...seats[seat],
        specialIndex: (seats[seat].specialIndex + dir + count) % count,
      };
      return { seats };
    }),

  shuffleCpu: (only) =>
    set((s) => {
      const seats = [...s.seats];
      const taken = new Set(
        participantSeats(s)
          .filter((i) => !isCpuSeat(s, i) || (only !== undefined && i !== only))
          .map((i) => seats[i].family)
      );
      for (const i of participantSeats(s)) {
        if (!isCpuSeat(s, i) || (only !== undefined && i !== only)) continue;
        // Prefere uma linhagem que ninguém está usando (como o "aleatório" do Smash)
        const free = TANK_FAMILY_IDS.filter((f) => !taken.has(f) && f !== seats[i].family);
        const pool = free.length > 0 ? free : TANK_FAMILY_IDS.filter((f) => f !== seats[i].family);
        const family = pool[Math.floor(Math.random() * pool.length)];
        const specials = familySpecials(family).length;
        seats[i] = {
          ...seats[i],
          family,
          specialIndex: Math.floor(Math.random() * Math.max(1, specials)),
        };
        taken.add(family);
      }
      return { seats };
    }),

  startMatch: () => {
    if (!hasHuman(get())) return;
    set((s) => ({ screen: "match", matchKey: s.matchKey + 1, ...FRESH_MATCH }));
  },

  rematch: () => set((s) => ({ matchKey: s.matchKey + 1, ...FRESH_MATCH })),

  // Depois de uma partida o lobby abre na montagem: mesmos jogadores e tanques
  backToLobby: () =>
    set({
      screen: "lobby",
      lobbyStep: "setup",
      setupFocus: SETUP_FOCUS.start,
      records: loadRecords(),
      ...FRESH_MATCH,
    }),

  setPaused: (paused, reason = "user", seat = null) =>
    set(
      paused
        ? { paused: true, pauseReason: reason, disconnectedSeat: seat, overlayFocus: 0 }
        : { paused: false, pauseReason: null, disconnectedSeat: null }
    ),

  setHud: (hud) => set({ hud }),
  setResult: (result) => set({ result }),

  flashHit: (seat) =>
    set((s) => {
      const hitFlash = [...s.hitFlash];
      hitFlash[seat] = performance.now();
      return { hitFlash };
    }),

  bumpObstacles: () => set((s) => ({ obstacleVersion: s.obstacleVersion + 1 })),
  setOverlayFocus: (overlayFocus) => set({ overlayFocus }),
  setShowFps: (showFps) => {
    saveShowFps(showFps);
    set({ showFps });
  },
  clearReturnToPlayMenu: () => {
    if (get().returnToPlayMenu) set({ returnToPlayMenu: false });
  },

  announce: (text, color = "#ffffff") => {
    const id = announcementId++;
    set((s) => ({
      announcements: [...s.announcements, { id, text, color }].slice(-MAX_ANNOUNCEMENTS),
    }));
    setTimeout(() => {
      set((s) => ({ announcements: s.announcements.filter((a) => a.id !== id) }));
    }, ANNOUNCEMENT_MS);
  },
}));
